import { useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Divider,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React from 'react'

import VisuallyHidden from '../../components/VisuallyHidden'
import { TICKET_ACTIVITY_QUERY } from '../../graphql/tickets'
import {
  TicketActivityEntry,
  TicketActivityPage,
  TicketCommentScope,
  TicketState,
  ticketActivityDetail,
  ticketActivityLabel,
  ticketCommentScopeLabel,
  ticketStateLabel
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import SectionCard from './SectionCard'
import When from './When'

interface TicketActivityProps {
  ticketId: string
  /** The ticket's createdAt, ISO. Bounds the query's `from`. */
  createdAt: string
  canInspect: boolean
}

const PAGE_SIZE = 50

/** `YYYY-MM-DD` in UTC — the only layout the BFF's time.Parse accepts. */
function isoDay(value: Date): string {
  return value.toISOString().slice(0, 10)
}

/** One day ahead of now, in UTC. See the `to` comment in the query below. */
function tomorrow(): Date {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() + 1)
  return d
}

/**
 * Per-action rendering of the audit payload's value.
 *
 * ONE table rather than a mapper plus a separate is-this-an-identifier list.
 * Those were two independently maintained enumerations of the same two action
 * names, and nothing linked them: adding a third translated action to one and
 * forgetting the other would wrap a translated Persian label in
 * `dir="ltr"` and render it left-to-right. Keyed by action, the two facts about
 * an action cannot drift apart.
 *
 * The audit payload stores protocol values — `RESOLVED`, `ADOTEL_INTERNAL`.
 * The state Chip at the top of this same page renders that value through
 * ticketStateLabel and the composer's scope Select through
 * ticketCommentScopeLabel, so showing the raw constant here would put two names
 * for one thing on one screen — and in Farsi would end a fully-formed Persian
 * sentence with an English SCREAMING_SNAKE constant.
 *
 * Mapping lives here rather than inside ticketActivityDetail so that helper
 * stays protocol-pure and independently testable.
 */
const DETAIL_RENDERERS: Record<string, (raw: string) => string> = {
  TransitionTicket: (raw) => ticketStateLabel(raw as TicketState),
  AddTicketComment: (raw) => ticketCommentScopeLabel(raw as TicketCommentScope)
}

/**
 * An action absent from the table renders its value unchanged and keeps LTR
 * isolation, because what is left is identifiers — user ids, product scopes —
 * which must not be reordered by a Farsi paragraph. Anything the table DOES
 * render is a translated label and must not be forced LTR.
 */
function detailFor(
  action: string,
  raw: string
): { text: string; isIdentifier: boolean } {
  const render = DETAIL_RENDERERS[action]
  return render
    ? { text: raw ? render(raw) : '', isIdentifier: false }
    : { text: raw, isIdentifier: true }
}

/**
 * The ticket's own history, read from the audit trail.
 *
 * WHY THIS IS ADMIN-GATED IN THE UI, and what that does NOT mean. The trail is
 * read through the shared `auditLogs` query. This panel gates on the same
 * `isAdmin && monitoring.view` its diagnostics sibling uses — but that is a UI
 * boundary, not a security one: the server-side check is role-family only and
 * carries no resource scoping, so it is both broader and coarser than this. See
 * BL-246; the gap is not this component's to close and must not be described as
 * though this gate closes it.
 *
 * Loaded on mount rather than behind a button, unlike the diagnostics sibling.
 * That sibling only mounts when the parent already knows content exists
 * (`hasDiagnosticContext`), so its button fetches a known-present blob. A
 * ticket ALWAYS has a history — at minimum its own creation — so a button here
 * has a predetermined outcome, which makes it a toll rather than a choice. It
 * also hides the panel from exactly the reader who needs it: someone arriving
 * cold at a ticket that changed hands three times has no reason to know the
 * trail exists. The cost is one more query on a screen that already fires
 * three, for the admin-only population this renders for at all.
 */
export default function TicketActivity({
  ticketId,
  createdAt,
  canInspect
}: TicketActivityProps) {
  const opened = new Date(createdAt)
  const { data, loading, error, refetch } = useQuery<{
    auditLogs: TicketActivityPage
  }>(TICKET_ACTIVITY_QUERY, {
    skip: !canInspect,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        page: 1,
        perPage: PAGE_SIZE,
        // An invalid createdAt would render "Invalid Date" and be rejected by
        // the BFF's parser, so one bad timestamp would look like the audit
        // store being down.
        from: isoDay(isNaN(opened.getTime()) ? new Date() : opened),
        // TOMORROW, not today. `to` is parsed as time.Parse("2006-01-02", …),
        // which yields MIDNIGHT UTC, and log-service turns it into an
        // Elasticsearch `Lte` on that exact instant — so today's date bounds
        // the query at today 00:00:00Z and excludes everything since.
        to: isoDay(tomorrow()),
        // ticketId selects the BFF's resource-scoped audit path, which first
        // asks ticket-service to authorize visibility. entityId is the broad
        // monitoring filter and must not be used with a route-controlled id.
        ticketId,
        // DESC: with a fixed first page, ASC would return the OLDEST 50 and
        // silently hide recent history — the inverse of what a reader wants
        // from a trail, and worse than showing nothing because it looks whole.
        sort: 'DATE_DESC'
      }
    }
  })

  if (!canInspect) return null

  const entries: TicketActivityEntry[] = data?.auditLogs?.logs || []
  const total = data?.auditLogs?.totalNumberOfLogs ?? 0

  return (
    <SectionCard heading={getTranslate('Activity')} level="h3" quiet>
      <Typography variant="body2" color="textSecondary">
        {getTranslate(
          'State changes, assignment and escalation. The conversation does not record them.'
        )}
      </Typography>

      {/* The success path was silent and the failure path spoke, via Alert's
          implicit role=alert — the same asymmetry the action bar fixed one
          component away. */}
      <VisuallyHidden live="polite">
        {loading
          ? getTranslate('Loading activity')
          : error
          ? ''
          : `${entries.length} ${getTranslate('activity entries loaded')}`}
      </VisuallyHidden>

      {loading && (
        <CircularProgress
          size={20}
          style={{ marginTop: 8 }}
          aria-label={getTranslate('Loading activity')}
        />
      )}

      {error && (
        <>
          <Alert severity="error" style={{ marginTop: 8 }}>
            {getTranslate('Could not load the activity trail.')}
          </Alert>
          {/* Without this a transient failure is terminal: the panel whose
              stated purpose is being readable when something has gone wrong
              would offer no control at all until a full page reload. */}
          <Button
            size="small"
            variant="outlined"
            style={{ marginTop: 8 }}
            onClick={() => refetch()}>
            {getTranslate('Retry')}
          </Button>
        </>
      )}

      {!loading && !error && entries.length === 0 && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8 }}>
          {getTranslate('No recorded activity for this ticket.')}
        </Typography>
      )}

      {entries.length > 0 && (
        <>
          {total > entries.length && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: 'block', marginTop: 8 }}>
              {getTranslate(
                'Showing the most recent {shown} of {total} entries.'
              )
                .replace('{shown}', String(entries.length))
                .replace('{total}', String(total))}
            </Typography>
          )}
          <Divider style={{ margin: '8px 0' }} />
          {/* role="list" restores what `list-style: none` removes in WebKit:
              Safari drops the implicit list role, and with it the "list, N
              items" announcement — which is load-bearing on a chronology. */}
          <ol role="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {entries.map((entry, index) => {
              const label = ticketActivityLabel(entry.action)
              const detail = detailFor(
                entry.action,
                ticketActivityDetail(entry.action, entry.logContent?.data)
              )
              const failed = entry.level === 'error'
              return (
                <li
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${entry.occurredAt}-${entry.action}-${index}`}
                  style={{ marginTop: 8 }}>
                  <Typography variant="body2">
                    {/* Optional: an audit record written by a job or a
                        service-to-service call carries no user. Unguarded this
                        threw inside render and took the WHOLE detail screen
                        down, not just this panel. */}
                    <bdi dir="ltr">
                      {entry.user?.email || getTranslate('system')}
                    </bdi>{' '}
                    {label ? getTranslate(label) : entry.action}
                    {detail.text && (
                      <>
                        {' — '}
                        {detail.isIdentifier ? (
                          <bdi dir="ltr">{detail.text}</bdi>
                        ) : (
                          detail.text
                        )}
                      </>
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={failed ? 'error' : 'textSecondary'}>
                    {/* No <time> wrapper here: When renders its own, and the
                        outer one predates that. Two nested <time> elements
                        expose one moment as two machine-readable timestamps,
                        and only the inner carries a title. */}
                    <When iso={entry.occurredAt} absolute />
                    {failed && ` · ${getTranslate('attempt failed')}`}
                  </Typography>
                </li>
              )
            })}
          </ol>
        </>
      )}
    </SectionCard>
  )
}
